import {
  AdminDatePickerInputModal,
  AdminDollarInput2,
  AdminHeader,
  AdminInput,
  AdminInputPlaceholder,
  AdminPhoneInput,
  AdminSingleBrandPicker,
  AdminSingleLocationPicker,
  AdminSpinner,
  AnimatedDiv,
  Button,
  CreateManualDeliveryAddressData,
  Div,
  ErrorToast,
  H5,
  P,
  Radio,
  SimpleBrandEntity,
  SimpleLocationEntity,
  SuccessToast,
  TextWithTooltipIcon,
  VDriveOffer,
  api,
  isPartner,
  showStringIfNotNull,
  useDeliveries,
  reportError,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { lazy, Suspense, useCallback, useState } from 'react';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router';
// Lazy-loaded: leaflet/maplibre access `window` at import time, which breaks
// Next.js prerender in the tracker app (this file is reachable via the
// @vgrubs/components barrel).
const DeliveryMapAddressComponent = lazy(() =>
  import('./components/DeliveryMapAddressComponent').then((m) => ({
    default: m.DeliveryMapAddressComponent,
  }))
);
import {
  AddressInput,
  ConfirmAddressInput,
} from './components/ManualDeliveryAddress';
import { VDriveAssignDriver } from './components/VDriveAssignDriver';

export const DeliveryCreateManual = () => {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [suite, setSuite] = useState('');
  const [notes, setNotes] = useState('');

  const [tip, setTip] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [assignment, setAssignment] = useState(1);

  const [assignDriverModal, setAssignDriverModal] = useState<
    VDriveOffer[] | null
  >(null);
  const [noDriverModal, setNoDriverModal] = useState(false);

  const [selectedLocation, setSelectedLocation] =
    useState<SimpleLocationEntity | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<SimpleBrandEntity | null>(
    null
  );

  const [changeAddressModal, setChangeAddressModal] = useState<boolean>(false);

  const [confirmAddressModal, setConfirmAddressModal] =
    useState<CreateManualDeliveryAddressData | null>(null);

  const [address, setAddress] = useState<any | null>(null);

  const [selectedDriver, setSelectedDriver] = useState(null);

  const [date, setDate] = useState<Date[]>([]);

  // const isEdit = location.pathname === ADMIN_EDIT_MANUAL_ORDER;

  const handleValueTipChange = useCallback((val: number) => {
    setTip(val);
  }, []);

  const handleValueSubTotalChange = useCallback((val: number) => {
    setSubTotal(val);
  }, []);

  const handleLocationsSubmit = (l: SimpleLocationEntity) => {
    setSelectedLocation(l);
    // setSelectedBrand(l.brand);
    // store(locationLSNode, JSON.stringify(l));
  };

  async function getAssignDriverOffers() {
    const body: any = {
      locationId: selectedLocation?.id,
      brandId: selectedBrand?.id ?? null,
      pickupAt:
        DateTime.fromJSDate(date[0])
          .setZone(selectedLocation?.timeZone, { keepLocalTime: true })
          .toUTC()
          .toISO() ?? null,
      notes: notes.length ? notes : null,
      charges: {
        tips: tip,
        subTotal: subTotal,
        total: tip + subTotal,
      },
      address: {
        zipCode: address.zipCode,
        address: address.address,
        streetNumber: address.streetNumber,
        city: address.city,
        state: address.state,
        country: address.country,
        unitNumber: suite.length ? suite : null,
        coordinates: address.coordinates,
      },
      customer: {
        name: name,
        phoneNumber: phoneNumber,
        phoneCode: parsePhoneNumber(phoneNumber)?.countryCallingCode,
      },
    };

    if (isPartner) delete body.brandId;

    try {
      setLoading(true);
      const { data } = await api.post(`/v1/deliveries/vdrive-offers`, body);

      if (data?.data?.length > 0) {
        setAssignDriverModal(data.data);
      } else {
        setNoDriverModal(true);
      }

      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function handleCreateOrder(driver?: any) {
    const body: any = {
      locationId: selectedLocation?.id,
      brandId: selectedBrand?.id ?? null,
      notes: notes.length ? notes : null,
      pickupAt:
        DateTime.fromJSDate(date[0])
          .setZone(selectedLocation?.timeZone, { keepLocalTime: true })
          .toUTC()
          .toISO() ?? null,
      shouldAutoAssign: assignment === 1 ? true : false,
      price: driver ? driver?.price : null,
      provider: driver ? driver?.provider : null,
      customer: {
        name: name,
        phoneNumber: phoneNumber,
        phoneCode: parsePhoneNumber(phoneNumber)?.countryCallingCode,
      },
      charges: {
        tips: tip,
        subTotal: subTotal,
        total: tip + subTotal,
      },
      address: {
        zipCode: address.zipCode,
        address: address.address,
        streetNumber: address.streetNumber,
        city: address.city,
        state: address.state,
        country: address.country,
        unitNumber: suite.length ? suite : null,
        coordinates: address.coordinates,
      },
    };

    if (isPartner) delete body.brandId;

    try {
      setLoading(true);
      const { data } = await api.post(`/v1/deliveries/manual`, body);

      SuccessToast(data.message);
      navigate(-1);

      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const handlePlaceOrder = () => {
    setSelectedDriver(null);

    if (selectedLocation === null) return ErrorToast('Please select location');
    if (!name) return ErrorToast('Please enter customer name.');
    if (!phoneNumber) return ErrorToast('Please enter your phone number');
    if (!isValidPhoneNumber(phoneNumber ?? ''))
      return ErrorToast('Please enter a valid phone number');
    if (address === null) return ErrorToast('Please select an address');
    if (!subTotal) return ErrorToast('Please enter subtotal');

    if (assignment === 2) {
      getAssignDriverOffers();
      return;
    }

    handleCreateOrder();
  };

  const handleSetAssignment = (a: number) => {
    setAssignment(a);
  };

  const handleSetAddress = (address: any) => {
    setAddress(address);
    setConfirmAddressModal(null);
    setChangeAddressModal(false);
  };

  const handleSetDriver = (d: any) => {
    setSelectedDriver(d);
    setAssignDriverModal(null);
    handleCreateOrder(d);
  };

  const locationAddress = `${showStringIfNotNull(
    address?.streetNumber
  )} ${showStringIfNotNull(address?.address, true)} ${showStringIfNotNull(
    address?.city,
    true
  )} ${showStringIfNotNull(address?.zipCode, true)} ${showStringIfNotNull(
    address?.state,
    true
  )} ${showStringIfNotNull(address?.country)}`;

  const { vDriveLocations } = useDeliveries((state) => state);


  return (
    <>
      {loading && <AdminSpinner />}

      <AdminHeader title="Create Manual Delivery"></AdminHeader>

      <AnimatedDiv
        $flex
        $gap="50px"
        $mt="32"
        $mtSM="20"
        $gapSM="20px"
        $columnSM
      >
        <Div $width="45%" $widthSM="100%">
          <Div $box $p="24px" $flex $column $gap="24px">
            <Div $flex $gap="8px" $gapSM="8px" $column>
              {/* {!isEdit && ( */}
              <AdminSingleLocationPicker
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                handleSubmit={(l) => handleLocationsSubmit(l)}
                customLocations={vDriveLocations}
              />
              {/* )} */}
              {/* {!isEdit && ( */}
              <AdminSingleBrandPicker
                selectedLocation={selectedLocation}
                selectedBrand={selectedBrand}
                setSelectedBrand={setSelectedBrand}
              />
              {/* )} */}
            </Div>

            <AdminInput
              value={name}
              onChange={({ target }: { target: HTMLInputElement }) =>
                setName(target.value)
              }
              label="Customer Full Name"
              placeholder="Enter here"
              $textCenter
            />

            <AdminPhoneInput
              label="Customer Phone Number"
              value={phoneNumber}
              onChange={setPhoneNumber}
              $textCenter
            />

            <AdminInputPlaceholder
              value={address ? locationAddress : null}
              label="Dropoff Address"
              placeholder="Your Dropoff address"
              $textCenter
              onClick={() => setChangeAddressModal(true)}
            />

            <Div $flex $columnSM $gap="24px">
              <AdminInput
                value={suite}
                onChange={({ target }: { target: HTMLInputElement }) =>
                  setSuite(target.value)
                }
                label="Unit/Apt/Suite"
                placeholder="Enter here"
                $textCenter
              />

              <Div $width="100%">
                <AdminDatePickerInputModal
                  format="MMM dd, yyyy hh:mm a"
                  options={{
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                    minDate: DateTime.now().toJSDate(),
                  }}
                  placeholder="ASAP"
                  $textCenter
                  label="Pickup Time"
                  onChange={setDate}
                  value={date}
                />
              </Div>
            </Div>

            <AdminInput
              label="Driver Notes"
              type="textarea"
              placeholder="Enter your notes here.."
              value={notes}
              onChange={(e: { target: HTMLTextAreaElement }) =>
                setNotes(e.target.value)
              }
              $textCenter
              $textareaHeight={100}
            />

            <Div $flex $gap="8px" $gapSM="8px" $column>
              <P $mb="0" $center>
                How would you like to assign the driver?
              </P>
              <Div
                $width="100%"
                $flex
                $pointer
                $alignCenter
                $gap="16px"
                $p="16px 20px"
                $box
                onClick={() => handleSetAssignment(1)}
              >
                <Radio checked={assignment === 1} />
                <TextWithTooltipIcon
                  bold
                  text="Assign Automatically"
                  tooltip="Based on cheapest and fastest."
                />
              </Div>
              <Div
                $width="100%"
                $flex
                $pointer
                $alignCenter
                $gap="16px"
                $p="16px 20px"
                $box
                onClick={() => handleSetAssignment(2)}
              >
                <Radio checked={assignment === 2} />

                <TextWithTooltipIcon
                  bold
                  text="Choose Fleet"
                  tooltip="Select fleet by yourself."
                />
              </Div>
            </Div>

            <Div $flex $columnSM $gap="24px">
              <AdminDollarInput2
                label="Tip ($)"
                onValueChange={handleValueTipChange}
                value={tip}
                $textCenter
                tooltip="Drivers respond faster when there is a tip."
              />
              <AdminDollarInput2
                label="Subtotal ($)"
                onValueChange={handleValueSubTotalChange}
                value={subTotal}
                $textCenter
                tooltip="What is the total value of the delivery?"
              />
            </Div>
            <Div>
              <Button $w="100%" $selected $mb="8" onClick={handlePlaceOrder}>
                Place Order
              </Button>
            </Div>
          </Div>
        </Div>
        <Div $width="55%" $widthSM="100%">
          <Div $box $p="24px">
            <Div $flex $alignCenter $between $mb="24">
              <H5 $mb="0">Map Address</H5>
            </Div>
            <Suspense fallback={null}>
              <DeliveryMapAddressComponent coordinates={address?.coordinates} />
            </Suspense>
          </Div>
        </Div>
      </AnimatedDiv>

      <VDriveAssignDriver
        modalIsOpen={assignDriverModal}
        setModalIsOpen={setAssignDriverModal}
        noDriverModal={noDriverModal}
        setNoDriverModal={setNoDriverModal}
        handleAssignDriver={handleSetDriver}
      />

      <AddressInput
        modalIsOpen={changeAddressModal}
        setModalIsOpen={setChangeAddressModal}
        setLoading={setLoading}
        setConfirmAddressModal={setConfirmAddressModal}
        title={'Dropoff Address'}
      />

      <ConfirmAddressInput
        modalIsOpen={confirmAddressModal}
        setModalIsOpen={setConfirmAddressModal}
        handleConfirmAddress={handleSetAddress}
        title={'Confirm Dropoff Address'}
      />
    </>
  );
};
